import { useEffect, useState, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const FAVORITES_KEY = "favoriteCities";

const getFavorites = () => {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY)) || [];
  } catch {
    return [];
  }
};

const setFavorites = (favorites) => {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
};

const CitySelector = ({ onCitySelect, selectedCity }) => {
  const [query, setQuery] = useState(selectedCity?.name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const [favorites, setFavoritesState] = useState(getFavorites());

  useEffect(() => {
    setFavoritesState(getFavorites());
  }, [selectedCity]);

  const addFavorite = (city) => {
    const updated = [...favorites.filter((c) => c.name !== city.name), city];
    setFavorites(updated);
    setFavoritesState(updated);
  };

  const removeFavorite = (city) => {
    const updated = favorites.filter((c) => c.name !== city.name);
    setFavorites(updated);
    setFavoritesState(updated);
  };

  const isFavorite = selectedCity && favorites.some((c) => c.name === selectedCity.name);

  // Update query when selectedCity changes
  useEffect(() => {
    if (selectedCity && typeof selectedCity.name === "string") {
      setQuery(selectedCity.name);
    } else if (typeof selectedCity === "string") {
      setQuery(selectedCity);
    } else {
      setQuery("");
    }
  }, [selectedCity]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (typeof query !== "string" || query.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch("/cities.json");
        const data = await response.json();

        const filteredCities = data.cities_list
          .filter((city) => {
            const cityName = typeof city.name === "string" ? city.name.toLowerCase() : "";
            const searchQuery = typeof query === "string" ? query.toLowerCase() : "";
            return cityName.includes(searchQuery);
          })
          .slice(0, 5);

        setSuggestions(filteredCities);
      } catch (error) {
        console.error("Error fetching city suggestions:", error);
        setSuggestions([]);
      }
      setLoading(false);
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSelect = (city) => {
    if (city && typeof city.name === "string") {
      onCitySelect(city);
      setQuery(city.name);
    } else {
      onCitySelect(null);
      setQuery("");
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setQuery("");
    setIsOpen(true);
    onCitySelect(null);
    inputRef.current?.focus();
  };

  return (
    <div>
      <div className="w-48 sm:w-64 relative flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search for a city..."
          className="w-full px-4 py-2 rounded-lg focus:outline-none pr-16" // add padding for icons
        />
        <div className="absolute right-0 top-0 bottom-0 flex items-center pr-2 gap-1">
          {(query || isOpen) && (
            <div
              onClick={handleClear}
              className="flex items-center px-1 cursor-pointer text-gray-400 hover:text-gray-600"
              title="Clear"
            >
              <XMarkIcon className="h-5 w-5" />
            </div>
          )}
          {loading && (
            <div className="flex items-center px-1">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          )}
          {selectedCity && (
            <button
              onClick={() =>
                isFavorite ? removeFavorite(selectedCity) : addFavorite(selectedCity)
              }
              className={`ml-1 px-2 py-1 rounded text-xs ${isFavorite ? "bg-yellow-200 text-yellow-800" : "bg-gray-200 text-gray-700"}`}
              title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
              {isFavorite ? "★" : "☆"}
            </button>
          )}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200">
          {suggestions.map((city, index) => (
            <button
              key={`${city.lat}-${city.lon}-${index}`}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 first:rounded-t-lg last:rounded-b-lg"
              onClick={() => handleSelect(city)}
            >
              <div className="font-medium">{city.name}</div>
              <div className="text-sm text-gray-400">{city.country}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CitySelector;
